<div class="appointment-details-page">
  @if (isLoading) {
    <app-loader></app-loader>
  }

  <!-- =====================================================
       HERO
  ====================================================== -->
  <section class="overview-hero">
    <div class="hero-copy">
      <span class="hero-kicker">Appointment Details</span>

      <h1>
        {{ Salon?.name || "Appointment" }}
      </h1>

      <p>
        Review your appointment time, salon location, services, pricing, and
        booking status.
      </p>
    </div>

    <div class="hero-actions">
      <button
        type="button"
        class="overview-btn overview-btn--light"
        (click)="navigateBack()"
      >
        Back
      </button>

      @if (canManageAppointment) {
        <button
          type="button"
          class="overview-btn overview-btn--light"
          (click)="modifyBooking()"
        >
          Modify Booking
        </button>

        <button
          type="button"
          class="overview-btn overview-btn--danger-light"
          (click)="openCancelConfirmation()"
        >
          Cancel Booking
        </button>
      }
    </div>
  </section>

  <!-- =====================================================
       KPI CARDS
  ====================================================== -->
  <section class="kpi-grid">
    <article class="kpi-card kpi-card--primary">
      <div>
        <span>Total Price</span>

        <strong>
          {{ appointmentDetails.total_price | currency }}
        </strong>

        <p>Total value of this appointment.</p>
      </div>

      <img [src]="assetUrl + 'icons/dollar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Status</span>

        <strong>
          {{ appointmentDetails.status || "Pending" }}
        </strong>

        <p>Current appointment booking status.</p>
      </div>

      <img [src]="assetUrl + 'icons/doc-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Services</span>

        <strong>
          {{ Services.length }}
        </strong>

        <p>Services included in this appointment.</p>
      </div>

      <img [src]="assetUrl + 'icons/menu-services.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Appointment Date</span>

        <strong>
          {{ formatDate(appointmentDetails.start_time) || "-" }}
        </strong>

        <p>
          {{ formatTime(appointmentDetails.start_time) }}
        </p>
      </div>

      <img [src]="assetUrl + 'icons/calendar-border.svg'" alt="" />
    </article>
  </section>

  <!-- =====================================================
       MAIN CONTENT
  ====================================================== -->
  <section class="main-grid">
    <!-- LEFT -->
    <article class="panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Booking Summary</span>

          <h2>
            {{ Salon?.name || "Salon Appointment" }}
          </h2>

          <p>
            {{ Salon?.address || "No address" }},
            {{ Salon?.city || "No city" }}
          </p>
        </div>

        <span
          class="status-pill"
          [ngClass]="statusClass(appointmentDetails.status)"
        >
          {{ appointmentDetails.status || "Pending" }}
        </span>
      </header>

      <!-- APPOINTMENT SUMMARY -->
      <article class="summary-card">
        <div class="date-card">
          <span>
            {{ formatDatePart(appointmentDetails.start_time, "MMM") }}
          </span>

          <strong>
            {{ formatDatePart(appointmentDetails.start_time, "DD") }}
          </strong>
        </div>

        <div class="summary-info">
          <h3>
            {{ Salon?.name || "Salon appointment" }}
          </h3>

          <p>
            {{ formatDate(appointmentDetails.start_time) }}
            at
            {{ formatTime(appointmentDetails.start_time) }}
          </p>

          <p>
            {{ Salon?.address || "No address" }},
            {{ Salon?.city || "No city" }}
          </p>
        </div>

        <strong class="summary-price">
          {{ appointmentDetails.total_price | currency }}
        </strong>
      </article>

      <!-- SERVICES -->
      <div class="service-panel">
        <header>
          <span class="panel-kicker">Services</span>
          <h2>Appointment Services</h2>
        </header>

        <div class="service-list">
          @for (service of Services; track service.id) {
            <div class="service-row">
              <div>
                <strong>
                  {{ service.service?.name || "Service" }}
                </strong>

                <span> {{ service.duration_snapshot || 0 }} mins </span>
              </div>

              <strong>
                {{ service.price_snapshot | currency }}
              </strong>
            </div>
          }

          <div class="service-row service-row--total">
            <div>
              <strong>Total</strong>
            </div>

            <strong>
              {{ appointmentDetails.total_price | currency }}
            </strong>
          </div>
        </div>
      </div>

      <!-- MANAGE BOOKING -->
      @if (canManageAppointment) {
        <div class="manage-panel">
          <div class="manage-panel__head">
            <div>
              <span class="panel-kicker"> Manage Appointment </span>

              <h2>Need to make a change?</h2>

              <p>
                Update your booking details or cancel the appointment if you can
                no longer attend.
              </p>
            </div>
          </div>

          <div class="manage-actions">
            <button
              type="button"
              class="manage-action-card"
              (click)="modifyBooking()"
            >
              <span class="manage-action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.8"
                    d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </span>

              <span class="manage-action-copy">
                <strong>Modify booking</strong>

                <small> Change the date, time, or appointment details. </small>
              </span>

              <span class="manage-action-arrow"> → </span>
            </button>

            <button
              type="button"
              class="manage-action-card manage-action-card--danger"
              (click)="openCancelConfirmation()"
            >
              <span class="manage-action-icon manage-action-icon--danger">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.8"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </span>

              <span class="manage-action-copy">
                <strong>Cancel booking</strong>

                <small>
                  Cancel this appointment if you can no longer attend.
                </small>
              </span>

              <span class="manage-action-arrow"> → </span>
            </button>
          </div>
        </div>
      }
    </article>

    <!-- RIGHT -->
    <aside class="side-column">
      <article class="panel">
        <header class="panel-head panel-head--stacked">
          <div>
            <span class="panel-kicker">Location</span>

            <h2>Salon Location</h2>

            <p>
              {{ Salon?.address || "No address" }},
              {{ Salon?.city || "No city" }}
            </p>
          </div>
        </header>

        <div class="map-shell">
          <glo-google-map
            [center]="{
              lat: latitude,
              lng: longitude,
            }"
            [zoom]="14"
            [markers]="mapMarkers"
          >
          </glo-google-map>
        </div>

        <button
          type="button"
          class="overview-btn overview-btn--dark w-full"
          (click)="getDirections()"
        >
          Get Directions
        </button>
      </article>

      @if (canManageAppointment) {
        <article class="panel help-panel">
          <span class="panel-kicker">Your Booking</span>

          <h2>Plans changed?</h2>

          <p>
            You can modify or cancel this appointment using the booking
            controls.
          </p>

          <button
            type="button"
            class="overview-btn overview-btn--light w-full"
            (click)="modifyBooking()"
          >
            Modify Booking
          </button>
        </article>
      }
    </aside>
  </section>

  <!-- =====================================================
       CANCEL CONFIRMATION MODAL
  ====================================================== -->
  @if (showCancelConfirmation) {
    <div class="modal-backdrop" (click)="closeCancelConfirmation()">
      <section
        class="cancel-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-title"
        (click)="$event.stopPropagation()"
      >
        <button
          type="button"
          class="modal-close"
          aria-label="Close"
          (click)="closeCancelConfirmation()"
        >
          ×
        </button>

        <div class="cancel-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.8"
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>

        <span class="modal-kicker"> Cancel Appointment </span>

        <h2 id="cancel-title">Cancel this booking?</h2>

        <p class="cancel-modal__description">
          You're about to cancel your appointment at
          <strong>{{ Salon?.name || "this salon" }}</strong>
          on
          <strong>
            {{ formatDate(appointmentDetails.start_time) }}
            at
            {{ formatTime(appointmentDetails.start_time) }} </strong
          >.
        </p>

        <div class="cancel-summary">
          <div>
            <span>Appointment</span>
            <strong>
              {{ formatDate(appointmentDetails.start_time) }}
            </strong>
          </div>

          <div>
            <span>Time</span>
            <strong>
              {{ formatTime(appointmentDetails.start_time) }}
            </strong>
          </div>

          <div>
            <span>Total</span>
            <strong>
              {{ appointmentDetails.total_price | currency }}
            </strong>
          </div>
        </div>

        <div class="cancel-modal__actions">
          <button
            type="button"
            class="overview-btn overview-btn--light"
            [disabled]="isCancelling"
            (click)="closeCancelConfirmation()"
          >
            Keep Appointment
          </button>

          <button
            type="button"
            class="overview-btn overview-btn--danger"
            [disabled]="isCancelling"
            (click)="confirmCancellation()"
          >
            {{ isCancelling ? "Cancelling..." : "Yes, Cancel Booking" }}
          </button>
        </div>
      </section>
    </div>
  }
</div>
